import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { NavigationTab } from '@/interfaces/registration/NavigationTab';
import { RegistrationData } from '@/interfaces/registration/RegistrationData';
import lodash from 'lodash';

@Module({ namespaced: true })

class Navigation extends VuexModule {
  public navigationTabs: Array<NavigationTab> = [
    { panelComponent: 'LoginPanel', name: 'Login Info' },
    { panelComponent: 'PersonalInfoPanel', name: 'Personal Info' },
    { panelComponent: 'AddressPanel', name: 'Address' },
    { panelComponent: 'QuestionsPanel', name: 'Questions' },
    { panelComponent: 'InterestsPanel', name: 'Interests' }
  ];

  public currentTabIndex = 0;

  public navigationStopped = false;

  public savingData = false;

  public validatingData = false;

  public submitingData = false;

  private postSaveAction = '';

  get currentTab (): NavigationTab {
    return this.filteredNavigationTabs[this.currentTabIndex];
  }

  get isFirstTab (): boolean {
    return this.currentTabIndex === 0;
  }

  get isLastTab (): boolean {
    return this.currentTabIndex === this.filteredNavigationTabs.length - 1;
  }

  @Mutation
  public changeCurrentTab (payload: {action: string; filteredNavigationTabs: Array<NavigationTab> }): void {
    switch (payload.action) {
      case 'SAVE':
        switch (this.postSaveAction) {
          case 'NEXT':
            if (this.currentTabIndex < payload.filteredNavigationTabs.length - 1) {
              payload.filteredNavigationTabs[this.currentTabIndex].completed = true;
              this.currentTabIndex++;
            }
            break;
          case 'PREVIOUS':
            if (this.currentTabIndex > 0) {
              this.currentTabIndex--;
            }
            break;
          case 'SUBMIT':
            this.submitingData = false;
            break;
        }
        this.postSaveAction = '';
        this.savingData = false;
        this.validatingData = false;
        break;
      case 'NEXT':
        this.postSaveAction = payload.action;
        this.savingData = true;
        this.validatingData = true;
        break;
      case 'PREVIOUS':
        this.postSaveAction = payload.action;
        this.savingData = true;
        this.validatingData = false;
        break;
      case 'SUBMIT':
        this.postSaveAction = payload.action;
        this.submitingData = true;
        this.savingData = true;
        this.validatingData = true;
        break;
      case 'RESET':
        this.postSaveAction = '';
        this.submitingData = false;
        this.savingData = false;
        this.validatingData = false;
        break;
    }
  }

  @Action
  public moveToNextTab (): void {
    this.context.commit('changeCurrentTab', { action: 'NEXT', filteredNavigationTabs: this.filteredNavigationTabs });
  }

  @Action
  public saveTabData (): void {
    this.context.commit('changeCurrentTab', { action: 'SAVE', filteredNavigationTabs: this.filteredNavigationTabs });
  }

  @Action
  public moveToPreviousTab (): void {
    this.context.commit('changeCurrentTab', { action: 'PREVIOUS', filteredNavigationTabs: this.filteredNavigationTabs });
  }

  @Action
  public submitForm (): void {
    this.context.commit('changeCurrentTab', { action: 'SUBMIT', filteredNavigationTabs: this.filteredNavigationTabs });
  }

  @Action
  public resetTabData (): void {
    this.context.commit('changeCurrentTab', { action: 'RESET', filteredNavigationTabs: this.filteredNavigationTabs });
  }

  @Mutation
  public stopNavigation (navigationStopped: boolean): void {
    this.navigationStopped = navigationStopped;
  }

  @Action
  public pauseNavigation (): void {
    this.context.commit('stopNavigation', true);
  }

  @Action
  public resumeNavigation (): void {
    this.context.commit('stopNavigation', false);
  }

  get filteredNavigationTabs (): Array<NavigationTab> {
    const registrationData = this.context.rootState.registration.registrationData as RegistrationData;
    return lodash.filter(this.navigationTabs, (tab) => {
      switch (tab.panelComponent) {
        case 'LoginPanel': {
          if (!registrationData.accountCreationRequired) {
            return false;
          }
          break;
        }

        case 'AddressPanel': {
          return lodash.find(registrationData.formConfig?.fields, (field) => (field.fieldType === 'Address' && field.fieldBehaviour !== 'Hidden')) !== undefined;
          break;
        }

        case 'InterestsPanel': {
          const tagField = lodash.find(registrationData.formConfig?.fields, (field) => (field.fieldType === 'Tag' && field.fieldBehaviour !== 'Hidden'));
          if (tagField === undefined || registrationData.tagSets === undefined || registrationData.tagSets.length === 0) {
            return false;
          }

          const visibleTagSetIds = tagField?.tags.map((tag) => parseInt(tag.value as string));
          return registrationData.tagSets.filter((tagSet) => {
            return visibleTagSetIds?.indexOf(tagSet.id as number) !== -1;
          }).length > 0;
          break;
        }

        case 'QuestionsPanel': {
          return (lodash.find(registrationData.formConfig?.fields, (field) => (field.fieldType === 'CommunityQuestions' && field.fieldBehaviour !== 'Hidden')) !== undefined && registrationData.questions !== undefined && registrationData.questions.length > 0) ||
            (registrationData.eventQuestions !== undefined && registrationData.eventQuestions.length > 0) ||
            (registrationData.volunteerQuestions !== undefined && registrationData.volunteerQuestions.length > 0) ||
            lodash.find(registrationData.formConfig?.fields, (field) => (field.fieldType === 'Education' && field.fieldBehaviour !== 'Hidden')) !== undefined ||
            lodash.find(registrationData.formConfig?.fields, (field) => (field.fieldType === 'WorkHistory' && field.fieldBehaviour !== 'Hidden')) !== undefined;
          break;
        }
      }

      return true;
    });
  }
}

export default Navigation;
